import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { approvalAPI, mediaAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '../components/ui/sheet';
import {
  CheckCircle2, XCircle, Clock, MessageSquare, User, FileText,
  Building2, Calendar, Globe, Image as ImageIcon, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLong, formatDateTimeLong } from '../lib/dateFormat';

const statusColors = {
  pending: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  approved: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  rejected: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
};

const platformInfo = {
  facebook: { color: '#1877F2', label: 'Facebook' },
  instagram: { color: '#E1306C', label: 'Instagram' },
  linkedin: { color: '#0A66C2', label: 'LinkedIn' },
  youtube: { color: '#FF0000', label: 'YouTube' },
  pinterest: { color: '#E60023', label: 'Pinterest' },
  gmb: { color: '#1A73E8', label: 'Google My Business' },
  substack: { color: '#FF6719', label: 'Substack' },
  twitter: { color: '#1DA1F2', label: 'Twitter/X' },
};

export default function ApprovalsPage() {
  const { currentWorkspace } = useWorkspace();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({});

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      approvalAPI.list(currentWorkspace.id, status)
        .then(res => setApprovals(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id, filter]);

  // Load media URLs for selected approval
  useEffect(() => {
    if (!selected?.post?.media_ids?.length || !currentWorkspace?.id) return;
    const newUrls = {};
    selected.post.media_ids.forEach(id => {
      newUrls[id] = mediaAPI.download(currentWorkspace.id, id);
    });
    setMediaUrls(newUrls);
  }, [selected, currentWorkspace?.id]);

  const handleAction = async (action) => {
    if (!selected) return;
    setActing(action);
    try {
      await approvalAPI.action(selected.id, { action, comment: comment || '' });
      toast.success(action === 'approve' ? 'Post approved!' : 'Post rejected — team has been notified');
      setSelected(null);
      setComment('');
      // Refresh
      const status = filter === 'all' ? null : filter;
      const res = await approvalAPI.list(currentWorkspace.id, status);
      setApprovals(res.data);
    } catch (err) {
      toast.error(`Action failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActing(null);
    }
  };

  const pendingCount = approvals.filter(a => a.status === 'pending').length;

  return (
    <div className="p-6 space-y-6" data-testid="approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {pendingCount > 0
              ? <>{pendingCount} post{pendingCount !== 1 ? 's' : ''} pending review</>
              : 'All caught up!'}
          </p>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="filter-pending">
            Pending {pendingCount > 0 && <Badge className="ml-1 text-[10px] h-4 px-1 bg-amber-500/20 text-amber-400">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="filter-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-secondary rounded-xl animate-pulse" />)}
        </div>
      ) : approvals.length === 0 ? (
        <div className="text-center py-20">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-medium">No approvals found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {filter === 'pending' ? 'All caught up!' : 'Submit posts for review to see them here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {approvals.map(approval => (
            <button
              key={approval.id}
              onClick={() => { setSelected(approval); setComment(''); }}
              className="w-full text-left p-4 rounded-xl border bg-card/60 backdrop-blur hover:border-primary/30 transition-colors group"
              data-testid={`approval-card-${approval.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-xs ${statusColors[approval.status]}`} data-testid={`approval-status-${approval.id}`}>
                      {approval.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                      {approval.status === 'approved' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {approval.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                      {approval.status}
                    </Badge>
                    {approval.client_group && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                        <Building2 className="h-2.5 w-2.5" /> {approval.client_group.name}
                      </span>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {formatDateLong(approval.created_at)}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{approval.post?.title || 'Untitled Post'}</p>
                  {approval.post?.content && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{approval.post.content}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    {approval.submitter && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <User className="h-2.5 w-2.5" /> {approval.submitter.name}
                      </span>
                    )}
                    {(approval.post?.platforms || []).slice(0, 3).map(p => (
                      <span key={p} className="text-[10px] capitalize text-muted-foreground">{p}</span>
                    ))}
                    {(approval.post?.media_ids || []).length > 0 && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <ImageIcon className="h-2.5 w-2.5" /> {approval.post.media_ids.length} media
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Review Sheet */}
      <Sheet open={!!selected} onOpenChange={open => { if (!open) { setSelected(null); setComment(''); } }}>
        <SheetContent className="w-[480px] sm:w-[560px] overflow-y-auto p-0" data-testid="approval-review-sheet">
          {selected && (
            <>
              {/* Header */}
              <div className="p-5 border-b sticky top-0 bg-background z-10">
                <SheetHeader>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-xs ${statusColors[selected.status]}`}>
                      {selected.status}
                    </Badge>
                    {selected.client_group && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {selected.client_group.name}
                      </span>
                    )}
                  </div>
                  <SheetTitle className="text-lg font-display">
                    {selected.post?.title || 'Untitled Post'}
                  </SheetTitle>
                </SheetHeader>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-5 space-y-5">
                  {/* Schedule + Type */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {(selected.post?.content_type || 'post').replace('_', ' ')}
                    </span>
                    {selected.post?.scheduled_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateTimeLong(selected.post.scheduled_at)}
                      </span>
                    )}
                  </div>

                  {/* Platforms */}
                  {(selected.post?.platforms || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selected.post.platforms.map(p => {
                        const info = platformInfo[p] || { color: '#888', label: p };
                        return (
                          <Badge key={p} variant="outline" className="text-xs gap-1.5 py-1 px-2.5">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: info.color }} />
                            {info.label}
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  <Separator />

                  {/* Caption / Content */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Caption / Content</h3>
                    <div className="p-4 rounded-lg bg-secondary/40 min-h-[100px]">
                      {selected.post?.content ? (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{selected.post.content}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No content</p>
                      )}
                    </div>
                  </div>

                  {/* Media */}
                  {(selected.post?.media_ids || []).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Media ({selected.post.media_ids.length} file{selected.post.media_ids.length > 1 ? 's' : ''})
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {selected.post.media_ids.map((id, i) => (
                          <div key={id} className="relative aspect-square rounded-lg overflow-hidden bg-secondary/60 border">
                            {mediaUrls[id] ? (
                              <img
                                src={mediaUrls[id]}
                                alt={`Media ${i + 1}`}
                                className="w-full h-full object-cover"
                                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <div className="absolute inset-0 flex items-center justify-center" style={{ display: mediaUrls[id] ? 'none' : 'flex' }}>
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submission note */}
                  {selected.note && (
                    <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15">
                      <p className="text-[10px] font-semibold text-amber-400 mb-1">Note from submitter:</p>
                      <p className="text-xs text-muted-foreground">{selected.note}</p>
                    </div>
                  )}

                  {/* Comments history */}
                  {(selected.comments || []).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Comments</h3>
                      <div className="space-y-2">
                        {selected.comments.map(c => (
                          <div key={c.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-secondary/40">
                            <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                            <div>
                              <p className="text-xs font-medium">{c.user_name}
                                <span className={`ml-1.5 text-[10px] ${c.action === 'approve' ? 'text-emerald-400' : 'text-rose-400'}`}>({c.action}d)</span>
                              </p>
                              {c.text && <p className="text-xs text-muted-foreground mt-0.5">{c.text}</p>}
                              <p className="text-[9px] text-muted-foreground/50 mt-1">{formatDateTimeLong(c.created_at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Submitter info */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    Submitted by <span className="font-medium text-foreground">{selected.submitter?.name || 'Unknown'}</span> on {formatDateLong(selected.created_at)}
                  </div>
                </div>
              </ScrollArea>

              {/* Action Bar — sticky bottom */}
              {selected.status === 'pending' && (
                <div className="sticky bottom-0 bg-background border-t p-4 space-y-3" data-testid="approval-action-bar">
                  <Textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Add feedback or reason for your decision (optional)..."
                    className="text-sm min-h-[60px]"
                    data-testid="approval-comment-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAction('approve')}
                      disabled={!!acting}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="approve-button"
                    >
                      {acting === 'approve' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleAction('reject')}
                      disabled={!!acting}
                      variant="outline"
                      className="flex-1 border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
                      data-testid="reject-button"
                    >
                      {acting === 'reject' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Request Changes
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
